import {
  collection,
  doc,
  setDoc,
  getDocs,
  getDoc,
  query,
  where,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import { db, storage } from "./firebase";

export const createCoachDocument = async (userId, userData) => {
  try {
    const coachData = {
      email: userData.email,
      name: userData.fullName,
      playBook: [],
      profile: {
        email: userData.email,
        name: userData.fullName,
        achievements: [],
        certifications: [],
        profileUrl: "",
        specialization: "",
        team: userData.teamId,
        emailVerified: false,
      },
    };

    await setDoc(doc(db, "coach", userId), coachData);
    return coachData;
  } catch (error) {
    throw error;
  }
};

export const updateEmailVerificationStatus = async (userId, isVerified) => {
  try {
    const coachRef = doc(db, "coach", userId);
    await updateDoc(coachRef, {
      "profile.emailVerified": isVerified,
    });
  } catch (error) {
    console.error("Error updating email verification status:", error);
    throw error;
  }
};

export const getPlayersByTeamId = async (teamId) => {
  try {
    const q = query(
      collection(db, "players"),
      where("profile.team", "==", teamId)
    );
    const querySnapshot = await getDocs(q);
    const players = [];

    querySnapshot.forEach((doc) => {
      players.push({ id: doc.id, ...doc.data() });
    });

    return players;
  } catch (error) {
    throw error;
  }
};

export const uploadPlaybookToFirestore = async (
  file,
  playbookName,
  coachId,
  playerIds = [],
  install = null
) => {
  try {
    const timestamp = Date.now();
    const sanitizedFileName = playbookName
      .replace(/[^a-z0-9]/gi, "_")
      .toLowerCase();

    const playbookId = `pb_${timestamp.toString().slice(-8)}`;

    const storageRef = ref(
      storage,
      `playbooks/${playbookId}/${timestamp}_${sanitizedFileName}`
    );

    const uploadResult = await uploadBytes(storageRef, file);
    const pdfUrl = await getDownloadURL(uploadResult.ref);

    // Create complete playbook object with all details
    const playbookData = {
      id: playbookId,
      title: playbookName,
      coachId: coachId,
      assignedPlayerIds: playerIds.length > 0 ? playerIds : [],
      createdAt: new Date(),
      uploadDate: new Date().toISOString().split("T")[0],
      category: null,
      duration: null,
      install: install ? parseFloat(install) : null,
      lastViewed: null,
      viewed: false,
      pdfUrl,
      fileSize: file.size,
      fileName: file.name,
      fileType: file.type,
    };

    const coachRef = doc(db, "coach", coachId);
    await updateDoc(coachRef, {
      playBook: arrayUnion(playbookData),
    });

    if (playerIds && playerIds.length > 0) {
      await assignPlaybookToPlayersInFirestore(
        playbookId,
        playerIds,
        playbookData
      );
    }

    return {
      id: playbookId,
      title: playbookName,
      coachId,
      assignedPlayerIds: playerIds.length > 0 ? playerIds : [],
      pdfUrl,
      uploadDate: new Date().toISOString().split("T")[0],
      fileSize: file.size,
      fileName: file.name,
      fileType: file.type,
      install: install ? parseFloat(install) : null,
    };
  } catch (error) {
    throw error;
  }
};

export const assignPlaybookToPlayersInFirestore = async (
  playbookId,
  playerIds,
  playbookData
) => {
  try {
    const playbookForPlayer = {
      id: playbookData.id ?? playbookId,
      title: playbookData.title ?? null,
      coachId: playbookData.coachId ?? null,
      category: playbookData.category ?? null,
      duration: playbookData.duration ?? null,
      install: playbookData.install ?? null,
      lastViewed: playbookData.lastViewed ?? null,
      pdfUrl: playbookData.pdfUrl ?? null,
      uploadDate: playbookData.uploadDate ?? null,
      createdAt: playbookData.createdAt ?? null,
      fileSize: playbookData.fileSize ?? null,
      fileName: playbookData.fileName ?? null,
      fileType: playbookData.fileType ?? null,
      viewed: false,
    };

    const updatePromises = playerIds.map(async (playerId) => {
      try {
        const playerRef = doc(db, "players", playerId);

        await updateDoc(playerRef, {
          playBook: arrayUnion(playbookForPlayer),
        });

        return { playerId, success: true };
      } catch (playerError) {
        return { playerId, success: false, error: playerError };
      }
    });

    await Promise.all(updatePromises);

    if (playbookData.coachId) {
      const coachRef = doc(db, "coach", playbookData.coachId);
      const coachDoc = await getDoc(coachRef);

      if (coachDoc.exists()) {
        const coachData = coachDoc.data();
        const coachPlaybooks = coachData.playBook || [];

        const updatedPlaybooks = coachPlaybooks.map((pb) => {
          if (pb.id === playbookId) {
            const currentAssignedPlayerIds = pb.assignedPlayerIds || [];
            const newPlayerIds = playerIds.filter(
              (id) => !currentAssignedPlayerIds.includes(id)
            );
            return {
              ...pb,
              assignedPlayerIds: [...currentAssignedPlayerIds, ...newPlayerIds],
            };
          }
          return pb;
        });

        await updateDoc(coachRef, {
          playBook: updatedPlaybooks,
        });
      }
    }
  } catch (error) {
    throw error;
  }
};

export const getPlayersByIds = async (playerIds = []) => {
  if (!Array.isArray(playerIds) || playerIds.length === 0) return [];
  try {
    const fetches = playerIds.map((id) => getDoc(doc(db, "players", id)));
    const snaps = await Promise.all(fetches);
    return snaps
      .filter((s) => s.exists())
      .map((s) => ({ id: s.id, ...s.data() }));
  } catch (error) {
    throw error;
  }
};

export const getPlayerByEmail = async (email) => {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    const q = query(
      collection(db, "players"),
      where("profile.email", "==", normalizedEmail)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    // Return the first matching player
    const playerDoc = querySnapshot.docs[0];
    return { id: playerDoc.id, ...playerDoc.data() };
  } catch (error) {
    throw error;
  }
};

export const invitePlayerByEmail = async (playerEmail, coachId, teamId) => {
  try {
    // Find the player by email
    const player = await getPlayerByEmail(playerEmail);

    if (!player) {
      throw new Error("No player found with this email address.");
    }

    // Check if player already has a team assigned
    const currentTeamId = player.profile?.team;
    if (currentTeamId && currentTeamId.trim() !== "") {
      throw new Error("This player is already assigned to a team.");
    }

    // Update the player's team ID
    const playerRef = doc(db, "players", player.id);
    await updateDoc(playerRef, {
      "profile.team": teamId,
    });

    return {
      success: true,
      player: {
        id: player.id,
        name: player.profile?.name || player.name || "Unknown",
        email: player.email,
      },
    };
  } catch (error) {
    throw error;
  }
};

export const removePlayerFromTeam = async (playerId, coachId) => {
  try {
    const playerRef = doc(db, "players", playerId);

    // Clear the team ID and playbooks from player document
    await updateDoc(playerRef, {
      "profile.team": "",
      "playBook": [],
    });

    // Remove player ID from all playbooks in coach document
    if (coachId) {
      const coachRef = doc(db, "coach", coachId);
      const coachDoc = await getDoc(coachRef);

      if (coachDoc.exists()) {
        const coachData = coachDoc.data();
        const playbooks = coachData.playBook || [];

        // Update each playbook to remove the player ID from assignedPlayerIds
        const updatedPlaybooks = playbooks.map(playbook => {
          if (playbook.assignedPlayerIds && playbook.assignedPlayerIds.includes(playerId)) {
            return {
              ...playbook,
              assignedPlayerIds: playbook.assignedPlayerIds.filter(id => id !== playerId)
            };
          }
          return playbook;
        });

        // Update the coach document with cleaned playbooks
        await updateDoc(coachRef, {
          playBook: updatedPlaybooks
        });
      }
    }

    return {
      success: true,
      playerId,
    };
  } catch (error) {
    throw error;
  }
};

export const removePlaybookFromFirestore = async (playbookId, coachId) => {
  try {
    // Get the coach document
    const coachRef = doc(db, "coach", coachId);
    const coachDoc = await getDoc(coachRef);

    if (!coachDoc.exists()) {
      throw new Error("Coach document not found");
    }

    const coachData = coachDoc.data();
    const playbooks = coachData.playBook || [];
    const teamId = coachData.profile?.team;

    if (!playbooks.find((pb) => pb.id === playbookId)) {
      throw new Error("Playbook not found");
    }

    // --- Fix: Do NOT rely on assignedPlayerIds (can be stale/out-of-sync). ---
    // Instead, fetch ALL players on this team and check their actual Firestore
    // documents for the playbook. This handles cases where assignedPlayerIds
    // missed a player (e.g. due to a failed write, race condition, or re-assignment
    // via a different code path), and also scales correctly with 45-50+ players.
    const allPlayers = teamId ? await getPlayersByTeamId(teamId) : [];

    // Only update players who actually have this playbook in their document
    const playersWithPlaybook = allPlayers.filter((player) =>
      (player.playBook || []).some((pb) => pb.id === playbookId)
    );

    if (playersWithPlaybook.length > 0) {
      const playerUpdatePromises = playersWithPlaybook.map(async (player) => {
        try {
          const playerRef = doc(db, "players", player.id);
          const updatedPlayerPlaybooks = (player.playBook || []).filter(
            (pb) => pb.id !== playbookId
          );
          await updateDoc(playerRef, { playBook: updatedPlayerPlaybooks });
          return { playerId: player.id, success: true };
        } catch (playerError) {
          console.error(
            `Error removing playbook from player ${player.id}:`,
            playerError
          );
          return { playerId: player.id, success: false, error: playerError };
        }
      });

      const results = await Promise.all(playerUpdatePromises);

      // Warn if any individual player update failed
      const failures = results.filter((r) => !r.success);
      if (failures.length > 0) {
        console.warn(
          `Playbook removed from coach but failed for ${failures.length} player(s):`,
          failures.map((f) => f.playerId)
        );
      }
    }

    // Remove playbook from coach document
    const updatedPlaybooks = playbooks.filter((pb) => pb.id !== playbookId);
    await updateDoc(coachRef, { playBook: updatedPlaybooks });

    // Delete the PDF file(s) from Firebase Storage.
    // The folder is named by playbookId, so we list everything inside it
    // and delete each file. Wrapped in try/catch so a Storage failure
    // never prevents the Firestore deletion from completing.
    try {
      const folderRef = ref(storage, `playbooks/${playbookId}`);
      const { items } = await listAll(folderRef);
      await Promise.all(items.map((fileRef) => deleteObject(fileRef)));
    } catch (storageError) {
      console.warn(
        `Firestore cleanup done, but Storage deletion failed for playbook ${playbookId}:`,
        storageError
      );
    }

    return {
      success: true,
      playbookId,
      removedFromPlayers: playersWithPlaybook.length,
    };
  } catch (error) {
    console.error("Error removing playbook:", error);
    throw error;
  }
};


export const syncOrphanedPlaybooks = async (coachId) => {
  console.log("Syncing orphaned playbooks for coach:", coachId);
  try {
    const coachRef = doc(db, "coach", coachId);
    const coachDoc = await getDoc(coachRef);

    if (!coachDoc.exists()) return { synced: 0 };

    const coachData = coachDoc.data();
    const teamId = coachData.profile?.team;

    if (!teamId) return { synced: 0 };

    // Build a set of valid playbook IDs currently on the coach
    const validIds = new Set(
      (coachData.playBook || []).map((pb) => pb.id).filter(Boolean)
    );

    // Fetch all players on this team
    const players = await getPlayersByTeamId(teamId);

    // Find players who have at least one orphaned playbook entry
    const playersToFix = players.filter((player) =>
      (player.playBook || []).some((pb) => pb.id && !validIds.has(pb.id))
    );

    if (playersToFix.length === 0) return { synced: 0 };

    // Remove orphaned entries and update each affected player
    const updatePromises = playersToFix.map(async (player) => {
      const cleanPlaybooks = (player.playBook || []).filter(
        (pb) => !pb.id || validIds.has(pb.id)
      );
      const playerRef = doc(db, "players", player.id);
      await updateDoc(playerRef, { playBook: cleanPlaybooks });
    });

    await Promise.all(updatePromises);

    console.log(
      `[PlayFast] Orphan sync: cleaned ${playersToFix.length} player document(s).`
    );
    return { synced: playersToFix.length };
  } catch (error) {
    // Silent — sync failure should never crash the dashboard
    console.warn("[PlayFast] Orphan sync failed silently:", error);
    return { synced: 0 };
  }
};
